import { useLocation, NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Card } from '@/shared/ui/Card'
import { StatusBadge } from '@/shared/ui/Badge'
import { Avatar } from '@/shared/ui/Avatar'
import { Check, X, Loader2 } from 'lucide-react'
import { Button } from '@/shared/ui/Button'
import { formatCurrency } from '@/shared/utils'
import { useRefunds, useApproveRefund, useRejectRefund, useEligibleRewardTrainers, useProcessReward } from '@/api/hooks'

export function TodoPage() {
  const { t } = useTranslation()
  const location = useLocation()
  const isRewards = location.pathname.includes('pending-rewards')
  const isSchool = location.pathname.startsWith('/school')

  return (
    <div className="space-y-5">
      <PageHeader title={t('todoPage.title')} breadcrumbs={[{label:t('todoPage.breadcrumbs.dashboard'),to:isSchool?'/school/overview':'/dashboard'},{label:t('todoPage.breadcrumbs.todo')}]}/>
      <div className="flex border-b border-gray-100 bg-white rounded-t-xl">
        <NavLink to={isSchool ? '/school/todo/refund' : '/dashboard/todo/refund'} className={({isActive})=>`px-5 py-3 text-sm font-medium border-b-2 ${isActive?'border-brand-500 text-brand-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>{t('todoPage.tabs.pendingRefunds')}</NavLink>
        {!isSchool && (
          <NavLink to="/dashboard/todo/pending-rewards" className={({isActive})=>`px-5 py-3 text-sm font-medium border-b-2 ${isActive?'border-brand-500 text-brand-600':'border-transparent text-gray-500 hover:text-gray-700'}`}>{t('todoPage.tabs.pendingRewards')}</NavLink>
        )}
      </div>
      {isRewards && !isSchool ? <PendingRewards/> : <PendingRefunds/>}
    </div>
  )
}

function PendingRefunds() {
  const { t } = useTranslation()
  const { data, isLoading } = useRefunds({ status:'pending' })
  const approve = useApproveRefund(); const reject = useRejectRefund()
  const rows = toRows(data)
  return (
    <Card padding={false}>
      {isLoading ? <Center/> : (
        <div className="overflow-x-auto">
        <table className="drivys-table w-full">
          <thead className="bg-gray-50"><tr><th>{t('todoPage.refunds.booking')}</th><th>{t('todoPage.refunds.reason')}</th><th>{t('todoPage.refunds.amount')}</th><th>{t('todoPage.refunds.actions')}</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <Empty cols={4} msg={t('todoPage.refunds.empty')}/> : rows.map((r:any) => (
              <tr key={r.id}>
                <td className="font-mono text-xs">#{r.booking_id}</td>
                <td className="text-sm truncate max-w-xs">{r.reason}</td>
                <td className="font-bold">{formatCurrency(r.amount ?? 0)}</td>
                <td>
                  <div className="flex gap-1">
                    <button onClick={()=>approve.mutate({id:r.id})} disabled={approve.isPending} className="w-7 h-7 flex items-center justify-center rounded-lg bg-green-50 text-green-600 hover:bg-green-100 disabled:opacity-50"><Check size={13}/></button>
                    <button onClick={()=>reject.mutate({id:r.id})} disabled={reject.isPending} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50"><X size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Card>
  )
}

function PendingRewards() {
  const { t } = useTranslation()
  const { data, isLoading } = useEligibleRewardTrainers({ status:'pending' })
  const process = useProcessReward()
  const rows = toRows(data)
  return (
    <Card padding={false}>
      {isLoading ? <Center/> : (
        <div className="overflow-x-auto">
        <table className="drivys-table w-full">
          <thead className="bg-gray-50"><tr><th>{t('todoPage.rewards.trainer')}</th><th>{t('todoPage.rewards.reward')}</th><th>{t('todoPage.rewards.status')}</th><th>{t('todoPage.rewards.actions')}</th></tr></thead>
          <tbody>
            {rows.length === 0 ? <Empty cols={4} msg={t('todoPage.rewards.empty')}/> : rows.map((e:any) => (
              <tr key={e.id}>
                <td><div className="flex items-center gap-2"><Avatar name={e.trainer_name ?? e.name ?? 'T'} size="xs"/><span className="font-medium text-gray-900">{e.trainer_name ?? e.name ?? '—'}</span></div></td>
                <td className="text-gray-700">{e.reward_name ?? '—'}</td>
                <td><StatusBadge status={e.status ?? 'pending'}/></td>
                <td><Button size="sm" icon={<Check size={13}/>} loading={process.isPending} onClick={()=>process.mutate({id:e.id,status:'APPROVED'})}>{t('todoPage.rewards.grant')}</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </Card>
  )
}

// Normalizes the various shapes the API can return into a flat array:
// [...], { data: [...] }, { data: { data: [...] } } (Laravel paginator), etc.
function toRows(d: any): any[] {
  if (Array.isArray(d)) return d
  if (Array.isArray(d?.data)) return d.data
  if (Array.isArray(d?.data?.data)) return d.data.data
  return []
}

const Center = () => <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-500" size={24}/></div>
const Empty = ({cols,msg}:{cols:number;msg:string}) => <tr><td colSpan={cols} className="text-center text-gray-400 py-12 text-sm">{msg}</td></tr>
